import React, { useState, useEffect, useCallback } from 'react';
import { approvalsAPI } from '../../utils/api';
import { Drawer } from '../shared/DrawerManager';
import RecruiterHeader from './RecruiterHeader';
import { useAuth } from '../../context/AuthContext';
import toastr from 'toastr';

const REQUEST_TYPE_CONFIG = {
  shortlist: { label: 'Shortlist Candidate', color: '#2563EB', bg: '#EFF6FF', desc: 'Approve moving this candidate to the shortlist' },
  interview: { label: 'Schedule Interview', color: '#7C3AED', bg: '#F5F3FF', desc: 'Approve scheduling an interview with this candidate' },
  offer: { label: 'Extend Offer', color: '#059669', bg: '#F0FDF4', desc: 'Approve sending a job offer to this candidate' },
  hire: { label: 'Hire Candidate', color: '#D97706', bg: '#FFFBEB', desc: 'Final approval to hire this candidate' },
  other: { label: 'Other', color: '#6B7280', bg: '#F9FAFB', desc: 'Custom approval request' }
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#D97706', bg: '#FFFBEB', icon: '⏳' },
  approved: { label: 'Approved', color: '#059669', bg: '#F0FDF4', icon: '✓' },
  rejected: { label: 'Rejected', color: '#DC2626', bg: '#FEF2F2', icon: '✕' },
  withdrawn: { label: 'Withdrawn', color: '#6B7280', bg: '#F9FAFB', icon: '↩' }
};

const ApprovalDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [allApprovals, setAllApprovals] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [responseNotes, setResponseNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const [pendingRes, allRes] = await Promise.all([
        approvalsAPI.getPending(),
        approvalsAPI.getAll()
      ]);
      setPendingApprovals(pendingRes.data.approvals || []);
      setAllApprovals(allRes.data.approvals || []);
    } catch (error) {
      console.error('Failed to load approvals:', error);
      toastr.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  const handleReview = (approval) => {
    setSelectedApproval(approval);
    setResponseNotes('');
    setShowReviewModal(true);
  };

  const handleApprove = async () => {
    if (!selectedApproval) return;
    setProcessing(true);
    try {
      await approvalsAPI.approve(selectedApproval._id, responseNotes.trim() || undefined);
      toastr.success('Request approved');
      setShowReviewModal(false);
      setSelectedApproval(null);
      loadApprovals();
    } catch (error) {
      toastr.error(error.response?.data?.error || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApproval || !responseNotes.trim()) {
      toastr.error('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    try {
      await approvalsAPI.reject(selectedApproval._id, responseNotes.trim());
      toastr.success('Request rejected');
      setShowReviewModal(false);
      setSelectedApproval(null);
      loadApprovals();
    } catch (error) {
      toastr.error(error.response?.data?.error || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatRelative = (date) => {
    if (!date) return '';
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return formatDate(date);
  };

  const TypeBadge = ({ type }) => {
    const cfg = REQUEST_TYPE_CONFIG[type] || REQUEST_TYPE_CONFIG.other;
    return (
      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}20` }}>
        {cfg.label}
      </span>
    );
  };

  const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', color: cfg.color, background: cfg.bg }}>
        <span>{cfg.icon}</span> {cfg.label}
      </span>
    );
  };

  const historyApprovals = allApprovals.filter(a => a.status !== 'pending');
  const myRequests = allApprovals.filter(a => a.requestedBy?._id === user?._id);

  const getVisibleApprovals = () => {
    if (activeTab === 'pending') return pendingApprovals;
    if (activeTab === 'history') return historyApprovals;
    if (activeTab === 'my_requests') return myRequests;
    return allApprovals;
  };

  const visibleApprovals = getVisibleApprovals();

  const renderApprovalCard = (approval, showActions = false) => {
    const candidate = approval.jobApplicationId?.candidateInfo;
    const candidateName = candidate ? `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() : 'Unknown';
    const candidateStatus = approval.jobApplicationId?.status;
    const typeCfg = REQUEST_TYPE_CONFIG[approval.requestType] || REQUEST_TYPE_CONFIG.other;

    return (
      <div key={approval._id} style={{ padding: '1.25rem 1.5rem', background: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', transition: 'box-shadow 0.15s', marginBottom: '0.75rem' }}
        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
        onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Header: Type + Status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
              <TypeBadge type={approval.requestType} />
              {approval.status !== 'pending' && <StatusBadge status={approval.status} />}
            </div>

            {/* Description */}
            <div style={{ fontSize: '0.8125rem', color: '#6B7280', marginBottom: '0.5rem' }}>
              {typeCfg.desc}
            </div>

            {/* Key details */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem', marginBottom: '0.75rem' }}>
              <div>
                <span style={{ fontSize: '0.6875rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Candidate</span>
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>{candidateName}</div>
              </div>
              <div>
                <span style={{ fontSize: '0.6875rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Position</span>
                <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>{approval.jobPostingId?.title || 'Unknown'}</div>
              </div>
              <div>
                <span style={{ fontSize: '0.6875rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Requested By</span>
                <div style={{ fontSize: '0.875rem', color: '#374151' }}>{approval.requestedBy?.fullName || approval.requestedBy?.email || 'Unknown'}</div>
              </div>
              <div>
                <span style={{ fontSize: '0.6875rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Approver</span>
                <div style={{ fontSize: '0.875rem', color: '#374151' }}>{approval.requestedFor?.fullName || approval.requestedFor?.email || 'Unknown'}</div>
              </div>
              {candidateStatus && (
                <div>
                  <span style={{ fontSize: '0.6875rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Candidate Status</span>
                  <div style={{ fontSize: '0.8125rem', color: '#374151' }}>{candidateStatus.replace(/_/g, ' ')}</div>
                </div>
              )}
              {(approval.jobApplicationId?.rating || approval.jobApplicationId?.ratings?.overall) && (
                <div>
                  <span style={{ fontSize: '0.6875rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rating</span>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#D97706' }}>{approval.jobApplicationId?.ratings?.overall || approval.jobApplicationId?.rating} / 5</div>
                </div>
              )}
              {approval.jobApplicationId?.assessmentScore !== undefined && approval.jobApplicationId?.assessmentScore !== null && (
                <div>
                  <span style={{ fontSize: '0.6875rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assessment</span>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: approval.jobApplicationId.assessmentScore >= 70 ? '#059669' : '#DC2626' }}>{approval.jobApplicationId.assessmentScore}%</div>
                </div>
              )}
            </div>

            {/* Message */}
            {approval.message && (
              <div style={{ padding: '0.625rem 0.75rem', background: '#F9FAFB', borderRadius: '6px', fontSize: '0.8125rem', color: '#4B5563', borderLeft: '3px solid #D1D5DB', marginBottom: '0.5rem' }}>
                "{approval.message}"
              </div>
            )}

            {/* Response notes (for history) */}
            {approval.responseNotes && (
              <div style={{ padding: '0.625rem 0.75rem', background: approval.status === 'approved' ? '#F0FDF4' : '#FEF2F2', borderRadius: '6px', fontSize: '0.8125rem', color: approval.status === 'approved' ? '#065F46' : '#991B1B', borderLeft: `3px solid ${approval.status === 'approved' ? '#10B981' : '#EF4444'}`, marginBottom: '0.5rem' }}>
                <strong>{approval.status === 'approved' ? 'Approval Note:' : 'Rejection Reason:'}</strong> {approval.responseNotes}
              </div>
            )}

            {/* Reviewer info (for history) */}
            {approval.reviewedBy && (
              <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.25rem' }}>
                {approval.status === 'approved' ? 'Approved' : 'Rejected'} by {approval.reviewedBy?.fullName || approval.reviewedBy?.email} on {formatDate(approval.reviewedAt)}
              </div>
            )}

            {/* Timestamp */}
            <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.25rem' }}>
              Requested {formatRelative(approval.requestedAt)}
            </div>
          </div>

          {/* Action button */}
          {showActions && approval.status === 'pending' && (
            <button onClick={() => handleReview(approval)} style={{ padding: '0.5rem 1rem', background: '#1E40AF', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.8125rem', whiteSpace: 'nowrap', flexShrink: 0 }}
              onMouseEnter={(e) => e.target.style.background = '#1E3A8A'}
              onMouseLeave={(e) => e.target.style.background = '#1E40AF'}
            >
              Review
            </button>
          )}
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'pending', label: 'Pending', count: pendingApprovals.length },
    { id: 'history', label: 'History', count: historyApprovals.length },
    { id: 'my_requests', label: 'My Requests', count: myRequests.length }
  ];

  return (
    <div>
      <RecruiterHeader currentView="approvals" />
      <div style={{ padding: '2rem', maxWidth: '960px', margin: '0 auto' }}>
        <h2 style={{ margin: '0 0 1.5rem', fontSize: '1.5rem', fontWeight: '600', color: '#111827' }}>Approvals</h2>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E5E7EB', marginBottom: '1.5rem' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              padding: '0.75rem 1.25rem', background: 'none', border: 'none', borderBottom: activeTab === tab.id ? '2px solid #1E40AF' : '2px solid transparent',
              marginBottom: '-2px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: activeTab === tab.id ? '600' : '400',
              color: activeTab === tab.id ? '#1E40AF' : '#6B7280', display: 'flex', alignItems: 'center', gap: '0.5rem'
            }}>
              {tab.label}
              {tab.count > 0 && (
                <span style={{ fontSize: '0.6875rem', padding: '1px 6px', borderRadius: '10px', background: activeTab === tab.id ? '#1E40AF' : '#E5E7EB', color: activeTab === tab.id ? '#fff' : '#6B7280', fontWeight: '600' }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280' }}>Loading approvals...</div>
        ) : visibleApprovals.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
            <p style={{ fontSize: '1rem', color: '#6B7280', margin: '0 0 0.5rem' }}>
              {activeTab === 'pending' ? 'No pending approval requests' : activeTab === 'history' ? 'No approval history yet' : 'You haven\'t made any approval requests'}
            </p>
            <p style={{ fontSize: '0.8125rem', color: '#9CA3AF', margin: 0 }}>
              {activeTab === 'pending' ? 'All approval requests have been reviewed' : 'Approval actions will appear here'}
            </p>
          </div>
        ) : (
          <div>
            {visibleApprovals.map(approval => renderApprovalCard(approval, activeTab === 'pending'))}
          </div>
        )}
      </div>

      {/* Review Drawer */}
      {showReviewModal && selectedApproval && (
        <Drawer
          isOpen={true}
          onClose={() => { setShowReviewModal(false); setSelectedApproval(null); setResponseNotes(''); }}
          title="Review Approval Request"
          width="md"
          footer={
            <>
              <button type="button" className="drawer-btn drawer-btn-secondary" onClick={() => { setShowReviewModal(false); setSelectedApproval(null); setResponseNotes(''); }} disabled={processing}>Cancel</button>
              <button type="button" style={{ padding: '0.625rem 1.25rem', background: processing || !responseNotes.trim() ? '#D1D5DB' : '#EF4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: processing || !responseNotes.trim() ? 'not-allowed' : 'pointer', fontWeight: '500', fontSize: '0.875rem' }}
                onClick={handleReject} disabled={processing || !responseNotes.trim()}
              >
                {processing ? 'Processing...' : 'Reject'}
              </button>
              <button type="button" style={{ padding: '0.625rem 1.25rem', background: processing ? '#D1D5DB' : '#059669', color: '#fff', border: 'none', borderRadius: '6px', cursor: processing ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
                onClick={handleApprove} disabled={processing}
              >
                {processing ? 'Processing...' : 'Approve'}
              </button>
            </>
          }
        >
          <div>
            {/* Type badge + description */}
            <div style={{ marginBottom: '1.25rem' }}>
              <TypeBadge type={selectedApproval.requestType} />
              <p style={{ fontSize: '0.8125rem', color: '#6B7280', margin: '0.5rem 0 0' }}>
                {(REQUEST_TYPE_CONFIG[selectedApproval.requestType] || REQUEST_TYPE_CONFIG.other).desc}
              </p>
            </div>

            {/* Details card */}
            <div style={{ background: '#F9FAFB', padding: '1rem 1.25rem', borderRadius: '8px', border: '1px solid #E5E7EB', marginBottom: '1.25rem' }}>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Position</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>{selectedApproval.jobPostingId?.title}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Candidate</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>
                    {selectedApproval.jobApplicationId?.candidateInfo?.firstName} {selectedApproval.jobApplicationId?.candidateInfo?.lastName}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Candidate Status</span>
                  <span style={{ fontSize: '0.8125rem', color: '#374151' }}>{(selectedApproval.jobApplicationId?.status || '').replace(/_/g, ' ')}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Requested By</span>
                  <span style={{ fontSize: '0.875rem', color: '#374151' }}>{selectedApproval.requestedBy?.fullName || selectedApproval.requestedBy?.email}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Requested</span>
                  <span style={{ fontSize: '0.8125rem', color: '#9CA3AF' }}>{formatDate(selectedApproval.requestedAt)}</span>
                </div>
                {selectedApproval.jobApplicationId?.resume?.fileUrl && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Resume</span>
                    <a href={selectedApproval.jobApplicationId.resume.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem', color: '#1E40AF', textDecoration: 'none', fontWeight: '500' }}>
                      {selectedApproval.jobApplicationId.resume.filename || 'View Resume'}
                    </a>
                  </div>
                )}
                {(selectedApproval.jobApplicationId?.rating || selectedApproval.jobApplicationId?.ratings?.overall) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Rating</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#D97706' }}>
                      {selectedApproval.jobApplicationId?.ratings?.overall || selectedApproval.jobApplicationId?.rating} / 5
                    </span>
                  </div>
                )}
                {selectedApproval.jobApplicationId?.assessmentScore != null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Assessment Score</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: selectedApproval.jobApplicationId.assessmentScore >= 70 ? '#059669' : '#DC2626' }}>
                      {selectedApproval.jobApplicationId.assessmentScore}%
                    </span>
                  </div>
                )}
                {selectedApproval.jobApplicationId?.assessmentCompletedAt && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Assessment Completed</span>
                    <span style={{ fontSize: '0.8125rem', color: '#9CA3AF' }}>{formatDate(selectedApproval.jobApplicationId.assessmentCompletedAt)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Interview Feedback / Notes from timeline */}
            {selectedApproval.jobApplicationId?.internalComments?.length > 0 && (
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.6875rem', color: '#6B7280', marginBottom: '0.5rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Team Feedback ({selectedApproval.jobApplicationId.internalComments.length})</div>
                <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {selectedApproval.jobApplicationId.internalComments
                    .slice()
                    .sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt))
                    .slice(0, 5)
                    .map((comment, idx) => (
                      <div key={idx} style={{ padding: '0.625rem 0.75rem', background: '#F9FAFB', borderRadius: '6px', fontSize: '0.8125rem', color: '#374151', borderLeft: '3px solid #D1D5DB' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <span style={{ fontWeight: '600', fontSize: '0.75rem', color: '#111827' }}>{comment.addedBy?.name || comment.addedBy?.fullName || comment.addedBy?.email || 'Team member'}</span>
                          <span style={{ fontSize: '0.6875rem', color: '#9CA3AF' }}>{formatDate(comment.addedAt)}</span>
                        </div>
                        <div>{comment.comment}</div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Request message */}
            {selectedApproval.message && (
              <div style={{ padding: '0.875rem 1rem', background: '#F0F9FF', borderRadius: '6px', border: '1px solid #BFDBFE', marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.6875rem', color: '#6B7280', marginBottom: '0.375rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recruiter's Message</div>
                <div style={{ fontSize: '0.875rem', color: '#1E40AF', whiteSpace: 'pre-wrap' }}>{selectedApproval.message}</div>
              </div>
            )}

            {/* Response notes */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151', fontSize: '0.875rem' }}>
                Your Response <span style={{ color: '#EF4444', fontWeight: '400', fontSize: '0.8125rem' }}>(required to reject)</span>
              </label>
              <textarea value={responseNotes} onChange={(e) => setResponseNotes(e.target.value)} placeholder="Add your notes, feedback, or reason..." rows={4}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
          </div>
        </Drawer>
      )}
    </div>
  );
};

export default ApprovalDashboard;
